import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import AddCourseToTrackModal from "../../../../Modals/AddCourseToTrackModal";
import AdminLearningPathdetailsCard from "../../../../cards/AdminLearningPathdetailsCard";
import { useReorderCoursesInLearningTrackMutation } from "../../../../../services/organization/adminLearningTrack";
import { useTranslation } from "react-i18next";

function reorderCourses(list, fromIndex, toIndex) {
  if (fromIndex === toIndex) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export default function Courses({
  courses,
  setCourses,
  deletedCoursesIds,
  setDeltedCoursesIds,
  learningTrackId,
}) {
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reorderCoursesInLearningTrack] =
    useReorderCoursesInLearningTrackMutation();
  const { t } = useTranslation(["learningPaths"]);

  const existingCourseIds = useMemo(
    () => new Set(courses.map((c) => c.id)),
    [courses],
  );

  function handleAddCourses(selectedCourses) {
    if (!selectedCourses?.length) return;
    const next = [...courses];
    const ids = new Set(next.map((c) => c.id));
    for (const course of selectedCourses) {
      if (!ids.has(course.id)) {
        ids.add(course.id);
        next.push(course);
      }
    }
    setCourses(next);
    setShowAddCourseModal(false);
  }

  async function deleteCourse(courseId) {
    try {
      const tempCourses = courses.filter((course) => course.id !== courseId);
      setDeltedCoursesIds([...deletedCoursesIds, courseId]);
      setCourses(tempCourses);
    } catch (error) {}
  }

  function handleDragStart(e, index) {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  }

  async function handleDrop(e, index) {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const previousCourses = courses;
    const reorderedCourses = reorderCourses(courses, draggedIndex, index);
    setCourses(reorderedCourses);
    setDraggedIndex(null);
    setDragOverIndex(null);

    if (!learningTrackId) return;

    try {
      await reorderCoursesInLearningTrack({
        learningTrackId,
        body: { course_ids: reorderedCourses.map((course) => course.id) },
      }).unwrap();
      toast.success(t("learningPaths:coursesReorderedSuccess"));
    } catch (error) {
      setCourses(previousCourses);
      toast.error(error?.data?.message || t("learningPaths:somethingWentWrong"));
    }
  }

  function handleDragEnd() {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }
  return (
    <div
      className="mt-2 admin-learning-path-edit__courses"
      id="nav-learning-track-courses"
      role="tabpanel"
      aria-labelledby="learning-track-courses-tab-tab"
    >
      <div className="container-fluid">
        <div className="row p-4">
          <div className="d-flex align-center flex-wrap admin-learning-path-edit__courses-toolbar">
            <div className="admin-learning-path-edit__courses-heading">
              <h1 className="font_20 font-extrabold">
                {t("learningPaths:coursesInTrack")}{" "}
              </h1>
            </div>
            <div className="ms-auto admin-learning-path-edit__courses-actions">
              <button
                type="button"
                className="btn btn-red-light-color font-15"
                onClick={() => setShowAddCourseModal(true)}
              >
                <span className="fas fa-plus fa-fw me-2"></span>
                {t("learningPaths:addCourseToTrack")}
              </button>
            </div>
            {showAddCourseModal && (
              <AddCourseToTrackModal
                isOpened={showAddCourseModal}
                onClose={() => setShowAddCourseModal(false)}
                onAddCourse={handleAddCourses}
                existingCourseIds={existingCourseIds}
              />
            )}
          </div>
          {courses?.length === 0 && (
            <div className="learinig-path-edit-courses-empty-state">
              <h3 className="title">{t("learningPaths:noCoursesinTrack")}</h3>
              <p className="description">
                {t("learningPaths:noCoursesinTrackHint")}{" "}
              </p>
            </div>
          )}
          <ul className="list-progressbar-cards">
            {courses?.map((course, index) => (
              <li
                key={`course-${course.id}`}
                className={`${draggedIndex === index ? "is-dragging" : ""} ${
                  dragOverIndex === index ? "is-drag-over" : ""
                }`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <hr className="border-dashed" />
                <span className="list-progressbar-cards-state">
                  {index + 1}
                </span>
                <button
                  type="button"
                  className="course-drag-handle flex-0"
                  draggable
                  aria-label={t("learningPaths:dragToReorder")}
                  onDragStart={(e) => handleDragStart(e, index)}
                >
                  <span className="fas fa-grip-vertical fa-fw font-color-grey"></span>
                </button>
                <AdminLearningPathdetailsCard
                  listDetails
                  course={course}
                  courseLabel={course.topics[0]?.title}
                  courseLabelColor={course.topics[0]?.color || "#C15258"}
                  title={course.title}
                  tutorName={course.tutor?.name}
                  courseImage={course.image_url}
                  onRemove={() => deleteCourse(course.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
